import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Download, Upload, Loader2, RefreshCw, Search } from 'lucide-react';
import { stockApi, downloadAuthed } from '../api';
import PageHeader from '../components/PageHeader';
import StockConfigCard from '../components/StockConfigCard';
import StockImportSummary from '../components/StockImportSummary';
import StockImportHistory from '../components/StockImportHistory';
import { useAuth } from '../context/AuthContext';

const fmt = (v) => (v ? new Date(/Z|[+-]\d\d:\d\d$/.test(v) ? v : `${v}Z`).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

export default function StockMaster() {
  const { theme } = useAuth();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState('');
  const [result, setResult] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);
  const fileRef = useRef();

  const load = () => stockApi.list().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (rows || []).filter(r => !s || `${r.branchCode} ${r.branchName} ${r.sku} ${r.product}`.toLowerCase().includes(s));
  }, [rows, q]);

  const run = async (kind, fn) => {
    setBusy(kind); setResult(null);
    try { setResult({ kind, ...(await fn()) }); load(); }
    catch (e) { setResult({ kind, ok: false, errors: [{ row: 0, reason: e.response?.data?.detail || 'Request failed' }] }); }
    finally { setBusy(''); setHistoryKey(k => k + 1); }
  };
  const onFile = (e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) run('upload', () => stockApi.import(f)); };
  const syncNow = () => run('sync', async () => {
    const r = await stockApi.syncNow();
    if (r.reportId) return { ok: r.status !== 'failed', status: r.status, total: r.total, written: r.written, failed: r.failed, errors: r.errors || [], reportId: r.reportId, reportFileName: r.reportFileName };
    return { ok: false, status: r.status === 'skipped' ? 'skipped' : 'failed', errors: [{ row: 0, reason: r.errorSummary || 'ERP email import failed' }] };
  });

  const btn = 'inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#E7E2D3] bg-white text-[13px] hover:bg-[#F7F4EA] disabled:opacity-50';
  return (
    <div data-testid="stock-master-page">
      <PageHeader title="Stock master" subtitle="Branch and Head Office available stock used to accept Tester orders" />
      <StockConfigCard theme={theme} />
      <div className="fm-card p-4 mb-4 flex flex-wrap items-center gap-2">
        <button className={btn} data-testid="stock-template-btn" onClick={() => downloadAuthed(stockApi.templateUrl, 'stock-template.xlsx')}><Download size={14} /> Template</button>
        <button className={btn} data-testid="stock-export-btn" onClick={() => downloadAuthed(stockApi.exportUrl, 'stock.xlsx')}><Download size={14} /> Export</button>
        <button className={btn} data-testid="stock-upload-btn" disabled={!!busy} onClick={() => fileRef.current?.click()}>
          {busy === 'upload' ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload Excel
        </button>
        <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={onFile} data-testid="stock-upload-input" />
        <button className={btn} data-testid="stock-sync-now-btn" disabled={!!busy} onClick={syncNow}>
          {busy === 'sync' ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Import from ERP email now
        </button>
        <span className="text-[12px] text-[#7a6f5c]">Available = Physical − Reserved − Blocked (never below 0). Good rows are saved; rows with errors are skipped and listed in the report.</span>
      </div>
      {result && <StockImportSummary result={result} />}
      <div className="fm-card overflow-hidden">
        <div className="p-3 border-b border-[#E7E2D3] relative">
          <Search size={14} className="absolute left-6 top-1/2 -translate-y-1/2 text-[#7a6f5c]" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search branch or SKU" data-testid="stock-search" className="fm-focus w-full max-w-[360px] border border-[#E7E2D3] rounded-md pl-8 pr-3 py-2 text-[13px]" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]" data-testid="stock-table">
            <thead><tr className="text-left text-[12px] text-[#7a6f5c]">
              {['Branch', 'SKU', 'Product', 'Physical', 'Reserved', 'Blocked', 'Available', 'Source', 'Updated'].map(h => <th key={h} className="px-4 py-2.5 font-medium">{h}</th>)}
            </tr></thead>
            <tbody>
              {rows === null ? <tr><td colSpan={9} className="py-8 text-center"><Loader2 className="animate-spin inline" size={18} /></td></tr>
                : filtered.length === 0 ? <tr><td colSpan={9} className="py-8 text-center text-[#7a6f5c]" data-testid="stock-empty">No stock loaded yet. Tester orders are rejected until stock is uploaded.</td></tr>
                : filtered.map(r => (
                  <tr key={`${r.branchCode}-${r.sku}`} className="border-t border-[#EFEADB]" data-testid={`stock-row-${r.branchCode}-${r.sku}`}>
                    <td className="px-4 py-2 whitespace-nowrap"><span className="font-mono">{r.branchCode}</span> <span className="text-[#7a6f5c]">{r.branchName}</span></td>
                    <td className="px-4 py-2 font-mono">{r.sku}</td>
                    <td className="px-4 py-2">{r.product}</td>
                    <td className="px-4 py-2 text-right">{r.physical}</td>
                    <td className="px-4 py-2 text-right">{r.reserved}</td>
                    <td className="px-4 py-2 text-right">{r.blocked}</td>
                    <td className="px-4 py-2 text-right font-semibold" data-testid={`stock-available-${r.branchCode}-${r.sku}`}>{r.available}</td>
                    <td className="px-4 py-2 text-[12px] text-[#7a6f5c]">{r.source}</td>
                    <td className="px-4 py-2 text-[12px] text-[#7a6f5c] whitespace-nowrap">{fmt(r.updatedAt)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
      <StockImportHistory refreshKey={historyKey} />
    </div>
  );
}
